import React, { useCallback, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router-dom";
import type { ServerError } from "@apollo/client";
import { ApolloError } from "@apollo/client";
import { isArray } from "@apollo/client/utilities";
import { isNil } from "ramda";

import { Button } from "affinityUI/elements/button";
import { Card } from "affinityUI/elements/card";
import { LayoutWrapper } from "affinityUI/structures/layout";

import { isRecord } from "../../helpers";

import errorAccess from "./assets/errorAccess.svg";
import errorGeneral from "./assets/errorGeneral.svg";
import errorInactive from "./assets/errorInactive.svg";
import errorNetwork from "./assets/errorNetwork.svg";
import errorNotFound from "./assets/errorNotFound.svg";
import { ErrorNoHospital } from "./errorNoHospital";
import { ErrorView } from "./errorView";

const ReloadButton: React.FC = () => {
  const handleClick = useCallback(() => {
    window.location.reload();
  }, []);

  return <Button onClick={handleClick}>Reload</Button>;
};

const getErrorType = (error: Error): React.ReactNode => {
  if (error.message === "Inactivity timeout") {
    return (
      <ErrorView heading="Session timeout" error={error} imgSrc={errorInactive} actions={<ReloadButton />}>
        <p>You have been logged out due to inactivity. Click reload to log back in.</p>
      </ErrorView>
    );
  }
  if (error instanceof ApolloError) {
    if (!isNil(error.graphQLErrors[0])) {
      if (error.graphQLErrors[0]?.extensions?.code === "FORBIDDEN") {
        return (
          <ErrorView heading="Access denied" error={error} imgSrc={errorAccess}>
            You do not have access to this page
          </ErrorView>
        );
      }
      if (error.graphQLErrors[0]?.extensions?.code === "PATIENT_NOT_FOUND") {
        return (
          <ErrorView heading="Patient not found" error={error} imgSrc={errorNotFound}>
            The patient you are looking for couldn’t be found
          </ErrorView>
        );
      }
    }
    if (!isNil(error.networkError) && !("response" in error.networkError)) {
      return (
        <ErrorView heading="No internet connection" error={error} imgSrc={errorNetwork} actions={<ReloadButton />}>
          <p>
            Slow or no internet connection
            <br />
            Please check your internet setting
          </p>
        </ErrorView>
      );
    }
    if (!isNil(error.networkError) && "response" in error.networkError) {
      if (error.networkError.statusCode === 400) {
        const networkError = error.networkError as ServerError;

        return isRecord(networkError.result) &&
          isArray(networkError.result?.errors) &&
          networkError.result?.errors[0] === "This user can't access graphql endpoint." ? (
          <ErrorNoHospital error={error} />
        ) : (
          <ErrorView heading="Bad request" error={error} imgSrc={errorGeneral} actions={<ReloadButton />}>
            Unexpected request has been sent to server
          </ErrorView>
        );
      } else {
        return (
          <ErrorView heading="Server error" error={error} imgSrc={errorGeneral} actions={<ReloadButton />}>
            <p>
              Something went wrong with our server
              <br />
              Please try again in a few moments
            </p>
          </ErrorView>
        );
      }
    }
  }
  return (
    <ErrorView heading="Something wrong" error={error} imgSrc={errorGeneral} actions={<ReloadButton />}>
      Something went wrong
    </ErrorView>
  );
};

export const ErrorComponent: React.FC<{ error: Error; resetErrorBoundary: (...args: Array<unknown>) => void }> = ({
  error,
}) => {
  const errorDisplay = useMemo(() => getErrorType(error), [error]);
  return (
    <LayoutWrapper>
      <Card>{errorDisplay}</Card>
    </LayoutWrapper>
  );
};

export const ErrorHandler: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  return (
    <ErrorBoundary FallbackComponent={ErrorComponent} resetKeys={[location.key]}>
      {children}
    </ErrorBoundary>
  );
};
